import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { CheckUpdatedEvent } from '@ancon/wildcat-types'

import { CheckReducerState } from '../types'
import { AddTipModalInitialState } from '../../app/components/addTipModal/constants'
import {
  AddTipState,
  TipModalType,
} from '../../app/components/addTipModal/types'

import { checkDetailsEntityAdapter } from './checkEntityAdapters'
import {
  cancelCheckPayment,
  checkAdyenAdditionalDetails,
  checkCreateAdyenPayment,
  fetchCheckAdyenPaymentMethods,
  fetchCheckDetails,
  fetchCheckOutletDetails,
  splitCheckItems,
} from './checkThunks'

const checkInitialState: CheckReducerState = {
  details: checkDetailsEntityAdapter.getInitialState(),

  currentCheckId: null,
  currentCheckOutlet: null,

  currentCheckOutletDetails: null,

  checkPaymentAttemptCount: 1,
  isFetchingCheckAdyenPaymentMethods: false,
  checkAdyenPaymentMethods: null,
  checkAdyenPaymentMethodsError: null,
  isCheckPaymentInProgress: false,
  isCancellingCheckPayment: false,

  checkTip: AddTipModalInitialState,
  checkCertainAmount: null,
}

const internalCheckDetailsAdapterSelectors =
  checkDetailsEntityAdapter.getSelectors()

const checkSlice = createSlice({
  name: 'check',
  initialState: checkInitialState,
  reducers: {
    setSelectedCheckId(state, action: PayloadAction<string>) {
      state.currentCheckId = action.payload
    },
    emitCheckUpdatedEvent(state, action: PayloadAction<CheckUpdatedEvent>) {
      checkDetailsEntityAdapter.updateOne(state.details, {
        id: action.payload.checkId,
        changes: action.payload.check,
      })
    },
    checkRetryPayment(state) {
      state.checkPaymentAttemptCount += 1
    },
    checkClearAdyenPaymentAttempts(state) {
      state.checkPaymentAttemptCount = 1
    },
    checkClearSelectedCheckId(state) {
      state.currentCheckId = null
    },
    checkUpdateTipState(state, action: PayloadAction<Partial<AddTipState>>) {
      state.checkTip = {
        ...state.checkTip,
        ...action.payload,
      }
    },
    checkSetTipModalVisibleType(state, action: PayloadAction<TipModalType>) {
      state.checkTip.visibleModalType = action.payload
    },
    checkSetCertainAmount(state, action: PayloadAction<number | null>) {
      state.checkCertainAmount = action.payload
    },
    setIsCheckPaymentInProgress(state, action: PayloadAction<boolean>) {
      state.isCheckPaymentInProgress = action.payload
    },
    setIsCancellingCheckPayment(state, action: PayloadAction<boolean>) {
      state.isCancellingCheckPayment = action.payload
    },
  },
  extraReducers(builder) {
    builder.addCase(fetchCheckDetails.fulfilled, (state, action) => {
      const { check, outlet } = action.payload
      checkDetailsEntityAdapter.upsertOne(state.details, check)
      state.currentCheckId = check.id
      state.currentCheckOutlet = outlet
    })
    builder.addCase(splitCheckItems.fulfilled, (state, action) => {
      const { checkId } = action.meta.arg
      const { created, updated } = action.payload

      const check = internalCheckDetailsAdapterSelectors.selectById(
        state.details,
        checkId,
      )

      if (check != null) {
        const newItems = check.items
          .map(item => {
            const updatedItem = updated.find(i => i.id === item.id)

            if (updatedItem != null) {
              return updatedItem
            }

            return item
          })
          .concat(created)

        checkDetailsEntityAdapter.updateOne(state.details, {
          id: checkId,
          changes: {
            items: newItems,
          },
        })
      }
    })
    builder.addCase(fetchCheckOutletDetails.fulfilled, (state, action) => {
      state.currentCheckOutletDetails = action.payload
    })
    builder.addCase(fetchCheckAdyenPaymentMethods.pending, state => {
      state.isFetchingCheckAdyenPaymentMethods = true
      state.checkAdyenPaymentMethodsError = null
    })
    builder.addCase(
      fetchCheckAdyenPaymentMethods.fulfilled,
      (state, action) => {
        const { paymentProviderData } = action.payload
        state.isFetchingCheckAdyenPaymentMethods = false
        state.checkAdyenPaymentMethods = paymentProviderData
      },
    )
    builder.addCase(fetchCheckAdyenPaymentMethods.rejected, (state, action) => {
      state.isFetchingCheckAdyenPaymentMethods = false
      state.checkAdyenPaymentMethodsError = action.error
    })
    builder.addCase(checkCreateAdyenPayment.fulfilled, (state, action) => {
      const { check } = action.payload
      if (check) {
        checkDetailsEntityAdapter.updateOne(state.details, {
          id: check.id,
          changes: check,
        })
      }
    })
    builder.addCase(checkAdyenAdditionalDetails.fulfilled, (state, action) => {
      if (action.payload) {
        const { check } = action.payload
        if (check) {
          checkDetailsEntityAdapter.updateOne(state.details, {
            id: check.id,
            changes: check,
          })
        }
      }
    })
    builder.addCase(cancelCheckPayment.pending, state => {
      state.isCancellingCheckPayment = true
    })
    builder.addCase(cancelCheckPayment.fulfilled, (state, action) => {
      const { check } = action.payload

      state.isCancellingCheckPayment = false
      state.isCheckPaymentInProgress = false

      if (check) {
        checkDetailsEntityAdapter.updateOne(state.details, {
          id: check.id,
          changes: check,
        })
      }
    })
    builder.addCase(cancelCheckPayment.rejected, state => {
      state.isCancellingCheckPayment = false
    })
  },
})

const checkReducer = checkSlice.reducer

export const {
  setSelectedCheckId,
  emitCheckUpdatedEvent,
  checkRetryPayment,
  checkClearAdyenPaymentAttempts,
  checkClearSelectedCheckId,
  checkUpdateTipState,
  checkSetTipModalVisibleType,
  checkSetCertainAmount,
  setIsCheckPaymentInProgress,
  setIsCancellingCheckPayment,
} = checkSlice.actions

export default checkReducer
